import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { ExportButton, ScopeButton } from '@monorepo/shared-lib';
import { Input, Select, Space } from 'antd';
import { ReactComponent as IconSupport } from 'assets/images/icons/iconSupport.svg';
import { StyledCard } from 'components/Custom/card';
import { StyledPopconfirm } from 'components/Custom/Popconfirm';
import {
  ScopeDropdownButton,
  ScopeEditCancelIcon,
  ScopeEditSaveButton,
} from 'components/Custom/ScopeDropdownButton';
import { CardTitleLargeSpan } from 'components/Custom/spans';
import { ScopeTable } from 'components/Custom/table';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { turnDrawerOff, turnDrawerOn } from 'store/app/actions';
import { DrawerType, ExcelTransactionAccountType } from 'store/app/enums';
import {
  getExcelTransactionAccountTypeLabel,
  getExcelTransactionAccountTypeOptions,
} from 'utils/display-enum-label';
import { DrawerData } from 'store/app/types';
import {
  deleteChartOfAccountRowRequest,
  fetchChartOfAccountsForOrganisationRequest,
  fetchChartOfAccountsTemplatesRequest,
  saveChartOfAccountsTemplateRequest,
  updateChartOfAccountsCustomRowRequest,
} from 'store/chart-of-accounts/actions';
import {
  CoaAccountRow,
  normalizeCoaAccountRow,
} from 'store/chart-of-accounts/models';
import { getChartOfAccountTemplates } from 'store/chart-of-accounts/selectors';
import { updateOrganisationChartOfAccounts } from 'store/organisation/actions';
import { shouldSkipChartOfAccountsBurst } from 'utils/chartOfAccountsFetchGuard';
import {
  getIs3rdPartyProvider,
  shouldFetchOrganisationProviderData,
  showsCoaAccountCodeColumn,
} from 'utils/get-provider-type';
import { Preference, PreferenceActions } from 'utils/preferences';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import { AddCustomCoaAccount } from './add-custom-coa-account';
import {
  CHART_OF_ACCOUNTS_TUTORIAL_HIDE,
  ChartOfAccountsGuide,
} from './chart-of-accounts-guide';
import { ChartOfAccountsPromotionBanner } from './chart-of-accounts-promotion-banner';
import { CreateCoaTemplate } from './create-coa-template';
import { EntityNarrowSidePanelChrome } from './entity-narrow-side-panel-chrome';
import { TAX_RATES_PROMOTION_SIDE_PANEL_WIDTH } from './tax-rates-promotion-banner';

const ENTITY_DETAILS_BREAKPOINT = 1500;
const ENTITY_DETAILS_HEIGHT_BREAKPOINT = 750;

/** Narrow layout: shrink below max-content table + inset for fixed AvatarGroup (EntityDetailsWrapper .top-wrapper). */
const COA_NARROW_MAIN_COLUMN_STYLE: React.CSSProperties = {
  flex: '1 1 auto',
  minWidth: 0,
  width: '100%',
  maxWidth: '100%',
  marginRight: '24px',
  boxSizing: 'border-box',
  paddingRight: 'clamp(72px, 16vw, 40px)',
};

const COA_NARROW_INNER_STYLE: React.CSSProperties = {
  padding: '0 40px',
  width: '100%',
  maxWidth: 1250,
  margin: '0 auto',
  boxSizing: 'border-box',
  minWidth: 0,
};

/** Options for the account type dropdown (same as transactions tab). */

export type { CoaAccountRow };

interface ChartOfAccountsTabProps {
  organisation?: any;
  handlePreferences: (action: PreferenceActions, preference: Preference) => any;
}

export const ChartOfAccountsTab: React.FC<ChartOfAccountsTabProps> = ({
  organisation,
  handlePreferences,
}) => {
  const { t } = useTranslation();
  const pwaMobileNav = usePwaMobileNavEnabled();
  const accountTypeOptions = React.useMemo(
    () => getExcelTransactionAccountTypeOptions(t),
    [t]
  );
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const coaWideLayout =
    windowWidth > ENTITY_DETAILS_BREAKPOINT &&
    windowHeight > ENTITY_DETAILS_HEIGHT_BREAKPOINT;
  const [showCoaPromoNarrowPanel, setShowCoaPromoNarrowPanel] =
    React.useState(false);
  React.useEffect(() => {
    if (coaWideLayout) {
      setShowCoaPromoNarrowPanel(false);
    } else {
      setShowCard(false);
    }
  }, [coaWideLayout]);
  const dispatch = useDispatch();
  const organisationId = organisation?.uuid ?? '';
  const is3rdPartyProvider = getIs3rdPartyProvider(organisation?.providerName);
  const showCoaAccountCodeColumn = showsCoaAccountCodeColumn(
    organisation?.providerName
  );

  const dataSource: CoaAccountRow[] = React.useMemo(() => {
    const rows = organisation?.chartOfAccounts;
    if (!Array.isArray(rows)) return [];
    return rows.map((row, index) => normalizeCoaAccountRow(row, index));
  }, [organisation?.chartOfAccounts]);
  const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[]>([]);
  const templates = useSelector(getChartOfAccountTemplates) || [];
  const hasTemplates = Array.isArray(templates) && templates.length > 0;

  // Ensure templates are loaded for the current organisation
  React.useEffect(() => {
    if (!organisationId) return;
    dispatch(fetchChartOfAccountsTemplatesRequest());
  }, [dispatch, organisationId]);

  // Fallback: on hard refresh while on the COA tab, ensure accounts load if
  // parent timing missed it. Wait until the organisation is actually in the
  // store (the COA success handler merges accounts onto it). Empty array is a
  // legitimate "loaded" result for new Excel entities, so the `chartOfAccountsLoaded`
  // flag is the source of truth.
  React.useEffect(() => {
    if (!organisationId) return;
    if (!organisation) return;
    if (!shouldFetchOrganisationProviderData(organisation)) return;
    if (
      organisation.chartOfAccountsLoaded ||
      (Array.isArray(organisation.chartOfAccounts) &&
        organisation.chartOfAccounts.length > 0)
    ) {
      return;
    }
    if (shouldSkipChartOfAccountsBurst(organisationId)) return;
    dispatch(fetchChartOfAccountsForOrganisationRequest(organisationId));
  }, [
    dispatch,
    organisationId,
    organisation,
    organisation?.chartOfAccountsLoaded,
    organisation?.chartOfAccounts,
  ]);

  const guidePanelHide = handlePreferences(PreferenceActions.GET, {
    name: CHART_OF_ACCOUNTS_TUTORIAL_HIDE,
  });
  const [showCard, setShowCard] = React.useState(
    !guidePanelHide && coaWideLayout && !pwaMobileNav
  );
  const [editingKey, setEditingKey] = React.useState<string | null>(null);
  const [editingDraftName, setEditingDraftName] = React.useState('');
  const [editingDraftAccountType, setEditingDraftAccountType] =
    React.useState('');

  const handleEdit = React.useCallback((record: CoaAccountRow) => {
    setEditingKey(record.id);
    setEditingDraftName(record.name);
    setEditingDraftAccountType(record.type ?? '');
  }, []);

  const handleCancelEdit = React.useCallback(() => {
    setEditingKey(null);
    setEditingDraftName('');
    setEditingDraftAccountType('');
  }, []);

  // Escape cancels edit (same as transactions tab)
  React.useEffect(() => {
    if (!editingKey) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleCancelEdit();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [editingKey, handleCancelEdit]);

  const handleSave = React.useCallback(
    (record: CoaAccountRow) => {
      const organisationId = organisation?.uuid;
      if (!organisationId) return;

      const isCustomRow = record.custom !== false;

      if (isCustomRow) {
        dispatch(
          updateChartOfAccountsCustomRowRequest(organisationId, {
            ...record,
            name: editingDraftName,
            type: editingDraftAccountType as ExcelTransactionAccountType,
            recommended: false,
            custom: true,
          })
        );
      } else {
        const currentAccounts: CoaAccountRow[] =
          organisation?.chartOfAccounts ?? [];
        const next = currentAccounts.map((row) =>
          row.id === record.id
            ? {
                ...row,
                name: editingDraftName,
                type: editingDraftAccountType as ExcelTransactionAccountType,
              }
            : row
        );
        dispatch(updateOrganisationChartOfAccounts(organisationId, next));
      }

      setEditingKey(null);
      setEditingDraftName('');
      setEditingDraftAccountType('');
    },
    [
      organisation?.uuid,
      organisation?.chartOfAccounts,
      editingDraftName,
      editingDraftAccountType,
      dispatch,
    ]
  );

  const handleDelete = React.useCallback(
    (record: CoaAccountRow) => {
      const organisationId = organisation?.uuid;
      if (!organisationId || !record.id) return;
      dispatch(deleteChartOfAccountRowRequest(organisationId, record.id));
    },
    [organisation?.uuid, dispatch]
  );

  const onAddCustomAccount = () => {
    if (!organisationId) return;

    const drawerData: DrawerData = {
      title: t('organisation.add-account'),
      type: DrawerType.CREATE_TAX_TEMPLATE,
      component: <AddCustomCoaAccount organisationId={organisationId} />,
      width: 420,
    };

    dispatch(turnDrawerOn(drawerData));
  };

  const onCreateCoaTemplate = () => {
    if (!organisationId) return;

    dispatch(fetchChartOfAccountsTemplatesRequest());

    let drawerData: DrawerData;

    if (selectedRowKeys.length === 0) {
      drawerData = {
        title: t('organisation.manage-templates'),
        type: DrawerType.CREATE_TAX_TEMPLATE,
        component: <CreateCoaTemplate mode="manage" />,
      };
    } else {
      const saveTemplate = (templateName: string) => {
        const accountIds = selectedRowKeys.map((key) => key.toString());
        dispatch(
          saveChartOfAccountsTemplateRequest(
            organisationId,
            templateName,
            accountIds
          )
        );
        setSelectedRowKeys([]);
        dispatch(turnDrawerOff());
      };

      drawerData = {
        title: t('organisation.create-template'),
        type: DrawerType.CREATE_TAX_TEMPLATE,
        component: (
          <CreateCoaTemplate mode="create" onSaveTemplate={saveTemplate} />
        ),
      };
    }

    dispatch(turnDrawerOn(drawerData));
  };

  const columns = React.useMemo(() => {
    const baseColumns: any[] = [];

    if (showCoaAccountCodeColumn) {
      baseColumns.push({
        title: t('organisation.code'),
        dataIndex: 'code',
        key: 'code',
        width: 120,
        render: (value: string | undefined) => {
          const displayText = value != null ? String(value).trim() : '';
          return (
            <span
              style={{
                display: 'block',
                width: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {displayText || '-'}
            </span>
          );
        },
      });
    }

    baseColumns.push(
      {
        title: t('organisation.account-name'),
        dataIndex: 'name',
        key: 'name',
        render: (value: unknown, record: CoaAccountRow) => {
          const isEditing = editingKey === record.id;
          if (isEditing) {
            return (
              <Input
                size="small"
                value={editingDraftName}
                onChange={(e) => setEditingDraftName(e.target.value)}
                style={{
                  width: '100%',
                  minWidth: 120,
                  fontSize: 12,
                  height: 26,
                  minHeight: 26,
                }}
                autoFocus
              />
            );
          }
          const displayText =
            String(value ?? record.name ?? '').trim() || '-';
          return (
            <span
              className="cell-dblclick"
              role="button"
              tabIndex={0}
              title={displayText}
              onDoubleClick={() => handleEdit(record)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleEdit(record);
                }
              }}
              style={{
                display: 'inline',
                whiteSpace: 'nowrap',
                cursor: 'default',
                color: 'var(--color-text-base, #042a2a)',
              }}
            >
              {displayText}
            </span>
          );
        },
      },
      {
        title: t('organisation.account-type'),
        dataIndex: 'type',
        key: 'type',
        width: 220,
        render: (value: string, record: CoaAccountRow) => {
          const isEditing = editingKey === record.id;
          if (isEditing) {
            return (
              <Select
                size="small"
                value={editingDraftAccountType || undefined}
                options={accountTypeOptions}
                onChange={(val) =>
                  setEditingDraftAccountType((val as string) ?? '')
                }
                style={{
                  width: '100%',
                  fontSize: 12,
                  height: 26,
                  minHeight: 26,
                }}
                dropdownStyle={{ minWidth: 220 }}
                popupMatchSelectWidth={false}
              />
            );
          }
          const displayLabel = getExcelTransactionAccountTypeLabel(
            String(value ?? ''),
            t
          );
          return (
            <span
              className="cell-dblclick"
              role="button"
              tabIndex={0}
              title={displayLabel}
              onDoubleClick={() => handleEdit(record)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleEdit(record);
                }
              }}
              style={{
                display: 'inline',
                whiteSpace: 'nowrap',
                cursor: 'default',
                color: 'var(--color-text-base, #042a2a)',
              }}
            >
              {displayLabel || '-'}
            </span>
          );
        },
      }
    );

    if (!is3rdPartyProvider) {
      baseColumns.push({
        title: '',
        key: 'actions',
        width: 120,
        render: (_: unknown, record: CoaAccountRow) => {
          const isCustom = record.custom !== false;
          const isEditing = editingKey === record.id;
          if (isEditing) {
            return (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  width: '100%',
                  gap: 6,
                }}
              >
                <ScopeEditSaveButton
                  type="default"
                  onClick={() => handleSave(record)}
                  aria-label={t('common.save')}
                >
                  {t('common.save')}
                </ScopeEditSaveButton>
                <ScopeEditCancelIcon
                  onClick={handleCancelEdit}
                  role="button"
                  tabIndex={0}
                  aria-label={t('common.cancel-editing')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleCancelEdit();
                    }
                  }}
                />
              </div>
            );
          }
          return (
            <ScopeDropdownButton
              label={
                isCustom ? (
                  t('common.edit')
                ) : (
                  <StyledPopconfirm
                    title={t('organisation.delete-this-account-title')}
                    onConfirm={() => handleDelete(record)}
                    okText={t('common.delete')}
                    cancelText={t('common.cancel')}
                    placement="left"
                  >
                    <span style={{ color: '#d9534f' }}>{t('common.delete')}</span>
                  </StyledPopconfirm>
                )
              }
              menu={{
                items: [
                  {
                    key: 'edit',
                    label: t('common.edit'),
                    icon: <EditOutlined style={{ fontSize: 16 }} />,
                    onClick: () => handleEdit(record),
                    disabled: !isCustom,
                  },
                  {
                    key: 'delete',
                    label: (
                      <StyledPopconfirm
                        title={t('organisation.delete-this-account-title')}
                        onConfirm={() => handleDelete(record)}
                        okText={t('common.delete')}
                        cancelText={t('common.cancel')}
                        placement="left"
                      >
                        <span>{t('other.delete-account')}</span>
                      </StyledPopconfirm>
                    ),
                    icon: <DeleteOutlined style={{ fontSize: 16 }} />,
                    danger: true,
                    onClick: () => {},
                  },
                ],
              }}
              onClick={() => (isCustom ? handleEdit(record) : undefined)}
              placement="bottomRight"
            />
          );
        },
      });
    }

    return baseColumns;
  }, [
    handleEdit,
    handleCancelEdit,
    handleSave,
    handleDelete,
    editingKey,
    editingDraftName,
    editingDraftAccountType,
    showCoaAccountCodeColumn,
    is3rdPartyProvider,
  ]);

  const coaTableCard = (
    <StyledCard
      style={{
        width: '100%',
        maxWidth: '100%',
        minWidth: coaWideLayout ? 600 : 0,
        ...(!coaWideLayout ? { overflowX: 'auto' as const } : {}),
      }}
    >
      <div
        style={{
          padding: '5px',
          paddingLeft: '5px',
          paddingTop: '5px',
          minWidth: coaWideLayout ? 500 : 0,
          margin: 'auto',
        }}
      >
        <ScopeTable
          rowSelection={
            !is3rdPartyProvider
              ? {
                  type: 'checkbox',
                  selectedRowKeys,
                  onChange: (keys: React.Key[]) => {
                    setSelectedRowKeys(keys);
                  },
                  columnTitle: (originalNode) =>
                    React.isValidElement(originalNode)
                      ? React.cloneElement(
                          originalNode as React.ReactElement<{
                            'aria-label'?: string;
                          }>,
                          { 'aria-label': t('common.select-all') }
                        )
                      : originalNode,
                  getCheckboxProps: (record: CoaAccountRow) => ({
                    'aria-label': record.name,
                  }),
                }
              : undefined
          }
          mainLocaleKey="chart-of-accounts"
          tableDataSource={dataSource}
          originalDataSource={dataSource}
          columns={columns}
          rowKey="id"
          pagination={false}
          rowsPerPage={0}
          enableColumnsCustomization={false}
          headerFontSize={12}
          transactionType=""
          reportId=""
          titleComponent={
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                width: '100%',
                minWidth: 0,
                maxWidth: '100%',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  flexWrap: 'wrap',
                  gap: 8,
                  minWidth: 0,
                  width: '100%',
                }}
              >
                <CardTitleLargeSpan
                  style={{ fontSize: 20, lineHeight: '32px' }}
                >
                  {t('organisation.chart-of-accounts')}{' '}
                  <span
                    style={{
                      fontSize: 12,
                      color: 'var(--color-text-muted, #5a6a6a)',
                      fontWeight: 400,
                    }}
                  >
                    {t('organisation.optional')}
                  </span>
                </CardTitleLargeSpan>
                {(!showCard || !is3rdPartyProvider) && (
                  <Space direction="vertical" align="end" size={10}>
                    {!showCard && !pwaMobileNav ? (
                      <ExportButton
                        type="text"
                        onClick={() => {
                          setShowCard(true);
                          handlePreferences(PreferenceActions.SET, {
                            name: CHART_OF_ACCOUNTS_TUTORIAL_HIDE,
                            value: false,
                          });
                        }}
                        icon={<IconSupport style={{ width: 20, height: 20 }} />} //@ts-ignore
                        isGuide
                      >
                        {t('organisation.open-guide')}
                      </ExportButton>
                    ) : null}
                    {!is3rdPartyProvider ? (
                      <Space size={8}>
                        <ScopeButton
                          type="default"
                          width="fit-content"
                          height="32px"
                          fontSize="13px"
                          lineheight="20px"
                          onClick={onCreateCoaTemplate}
                          disabled={
                            !organisationId ||
                            (selectedRowKeys.length === 0 && !hasTemplates)
                          }
                        >
                          {selectedRowKeys.length === 0
                            ? t('organisation.manage-templates')
                            : t('organisation.create-template')}
                          {selectedRowKeys.length > 0
                            ? ` (${selectedRowKeys.length})`
                            : ''}
                        </ScopeButton>
                        <ScopeButton
                          type="primary"
                          width="fit-content"
                          height="32px"
                          fontSize="13px"
                          lineheight="20px"
                          onClick={onAddCustomAccount}
                          disabled={!organisationId}
                        >
                          <PlusOutlined style={{ marginRight: 6 }} />
                          {t('organisation.add-account')}
                        </ScopeButton>
                      </Space>
                    ) : null}
                  </Space>
                )}
              </div>
              <p
                style={{
                  margin: 0,
                  paddingTop: 16,
                  fontSize: 14,
                  lineHeight: 1.7,
                  color: 'var(--color-text-muted, #5a6a6a)',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 400,
                }}
              >
                {t('organisation.before-you-scan-coa-info')}
              </p>
            </div>
          }
          rowHeight={40}
          showSizeChanger
          getNewPageSize={() => {}}
          scroll={{ x: 'max-content' }}
          fontSize={12}
        />
      </div>
    </StyledCard>
  );

  return (
    <div
      className="entity-chart-of-accounts-tab"
      style={{ minHeight: 'calc(100vh - 120px)' }}
    >
      <div
        className={`chart-of-accounts-tab-wrapper${
          !coaWideLayout ? ' chart-of-accounts-tab-wrapper--narrow' : ''
        }`}
      >
        {showCard && !pwaMobileNav ? (
          <ChartOfAccountsGuide
            setShowCard={setShowCard}
            handlePreferences={handlePreferences}
          />
        ) : null}
        {coaWideLayout ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              gap: 20,
              alignContent: 'end',
            }}
          >
            {coaTableCard}
            <ChartOfAccountsPromotionBanner
              organisation={organisation}
              cardWidth={TAX_RATES_PROMOTION_SIDE_PANEL_WIDTH}
            />
          </div>
        ) : (
          <div className="entity-overview-tab entity-details-narrow-side-shell">
            <div style={COA_NARROW_MAIN_COLUMN_STYLE}>
              <div style={COA_NARROW_INNER_STYLE}>{coaTableCard}</div>
            </div>
            <EntityNarrowSidePanelChrome
              panelOpen={showCoaPromoNarrowPanel}
              onOpenPanel={() => setShowCoaPromoNarrowPanel(true)}
              onClosePanel={() => setShowCoaPromoNarrowPanel(false)}
              tabLabel={t('organisation.chart-of-accounts-side-panel-tab')}
              panelWidthPx={TAX_RATES_PROMOTION_SIDE_PANEL_WIDTH}
            >
              <ChartOfAccountsPromotionBanner
                organisation={organisation}
                cardWidth={TAX_RATES_PROMOTION_SIDE_PANEL_WIDTH}
              />
            </EntityNarrowSidePanelChrome>
          </div>
        )}
      </div>
    </div>
  );
};
